import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework3Question.css';
import './Homework3Question4.css';

const Homework3Question4: React.FC = () => {
  const [image1Original, setImage1Original] = useState<string | null>(null);
  const [image1Processed, setImage1Processed] = useState<string | null>(null);
  const [image2Original, setImage2Original] = useState<string | null>(null);
  const [image2Processed, setImage2Processed] = useState<string | null>(null);
  const [loading, setLoading] = useState({ img1: false, img2: false });
  const [error, setError] = useState<string | null>(null);
  
  // Store TIF data for processing
  const [image1TifData, setImage1TifData] = useState<string | null>(null);
  const [image2TifData, setImage2TifData] = useState<string | null>(null);

  // Auto-load sample images on component mount
  useEffect(() => {
    const loadSampleImages = async () => {
      try {
        // Load first sample image - JPG for display, TIF for processing
        const responseJpg1 = await fetch('/sample-images/HW3_4_1.jpg');
        const blobJpg1 = await responseJpg1.blob();
        const readerJpg1 = new FileReader();
        readerJpg1.onload = (e) => {
          setImage1Original(e.target?.result as string);
        };
        readerJpg1.readAsDataURL(blobJpg1);

        const responseTif1 = await fetch('/sample-images/HW3_4_1.tif');
        const blobTif1 = await responseTif1.blob();
        const readerTif1 = new FileReader();
        readerTif1.onload = (e) => {
          setImage1TifData(e.target?.result as string);
        };
        readerTif1.readAsDataURL(blobTif1);

        // Load second sample image - JPG for display, TIF for processing
        const responseJpg2 = await fetch('/sample-images/HW3_4_2.jpg');
        const blobJpg2 = await responseJpg2.blob();
        const readerJpg2 = new FileReader();
        readerJpg2.onload = (e) => {
          setImage2Original(e.target?.result as string);
        };
        readerJpg2.readAsDataURL(blobJpg2);

        const responseTif2 = await fetch('/sample-images/HW3_4_2.tif');
        const blobTif2 = await responseTif2.blob();
        const readerTif2 = new FileReader();
        readerTif2.onload = (e) => {
          setImage2TifData(e.target?.result as string);
        };
        readerTif2.readAsDataURL(blobTif2);
      } catch (err) {
        console.error('Failed to load sample images:', err);
        setError('Failed to load sample images');
      }
    };
    loadSampleImages();
  }, []);

  const handleImage1Upload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target?.result as string;
        setImage1Original(data);
        setImage1TifData(data); // Use same data for processing
        setImage1Processed(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImage2Upload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target?.result as string;
        setImage2Original(data);
        setImage2TifData(data); // Use same data for processing
        setImage2Processed(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const processImage = async (tifData: string, imageNumber: 1 | 2) => {
    setLoading(prev => ({ ...prev, [`img${imageNumber}`]: true }));
    setError(null);

    try {
      const base64Image = tifData.split(',')[1];

      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework3_q4',
        {
          image: base64Image,
          operation: 'histogram_equalization'
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        }
      );

      if (response.data.success) {
        const processedData = `data:image/png;base64,${response.data.processed_image}`;
        if (imageNumber === 1) {
          setImage1Processed(processedData);
        } else {
          setImage2Processed(processedData);
        }
      } else {
        setError(response.data.error || 'Processing failed');
      }
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(prev => ({ ...prev, [`img${imageNumber}`]: false }));
    }
  };

  const applyEqualizationImage1 = () => {
    if (!image1TifData) {
      setError('Please upload Image 1 first');
      return;
    }
    processImage(image1TifData, 1);
  };

  const applyEqualizationImage2 = () => {
    if (!image2TifData) {
      setError('Please upload Image 2 first');
      return;
    }
    processImage(image2TifData, 2);
  };

  const applyBoth = async () => {
    if (!image1TifData || !image2TifData) {
      setError('Please upload both images first');
      return;
    }
    await Promise.all([
      processImage(image1TifData, 1),
      processImage(image2TifData, 2)
    ]);
  };

  return (
    <div className="hw3-question-container hw3-q4" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/homework/3" className="back-button">
          ← Back to Homework 3
        </Link>

        <header className="question-header">
          <div className="badge">Question 4</div>
          <h1>Dual Image Comparison</h1>
          <p className="subtitle">Side-by-side histogram equalization on two images</p>
        </header>

        <div className="algorithm-info">
          <h3>Objective</h3>
          <p>
            Compare histogram equalization results on two different test images side-by-side. This allows for
            visual analysis of how the algorithm performs on different image characteristics and content types.
          </p>
        </div>

        <div className="controls-section">
          <div className="dual-controls">
            <div className="image-control">
              <h4>Image 1 Controls</h4>
              <div className="button-group">
                <label htmlFor="image1-upload" className="upload-label compact">
                  <span className="upload-icon">📁</span>
                  <span>Upload 1</span>
                  <input
                    id="image1-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImage1Upload}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  onClick={applyEqualizationImage1}
                  disabled={!image1Original || loading.img1}
                  className="process-button compact"
                >
                  {loading.img1 ? 'Processing...' : 'Equalize 1'}
                </button>
              </div>
            </div>

            <div className="divider"></div>

            <div className="image-control">
              <h4>Image 2 Controls</h4>
              <div className="button-group">
                <label htmlFor="image2-upload" className="upload-label compact">
                  <span className="upload-icon">📁</span>
                  <span>Upload 2</span>
                  <input
                    id="image2-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImage2Upload}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  onClick={applyEqualizationImage2}
                  disabled={!image2Original || loading.img2}
                  className="process-button compact"
                >
                  {loading.img2 ? 'Processing...' : 'Equalize 2'}
                </button>
              </div>
            </div>
          </div>

          <div className="batch-control">
            <button
              onClick={applyBoth}
              disabled={!image1Original || !image2Original || loading.img1 || loading.img2}
              className="process-button batch"
            >
              Process Both Images
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="dual-images-grid">
          {/* Image 1 Section */}
          <div className="dual-section">
            <h3 className="section-title">Image 1</h3>
            <div className="image-pair">
              <div className="image-panel compact">
                <h4>Original</h4>
                <div className="image-container small">
                  {image1Original ? (
                    <img src={image1Original} alt="First original" />
                  ) : (
                    <div className="placeholder">Upload Image 1</div>
                  )}
                </div>
              </div>
              <div className="image-panel compact">
                <h4>Equalized</h4>
                <div className="image-container small">
                  {image1Processed ? (
                    <img src={image1Processed} alt="First equalized" />
                  ) : (
                    <div className="placeholder">
                      {loading.img1 ? 'Processing...' : 'Not processed'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Image 2 Section */}
          <div className="dual-section">
            <h3 className="section-title">Image 2</h3>
            <div className="image-pair">
              <div className="image-panel compact">
                <h4>Original</h4>
                <div className="image-container small">
                  {image2Original ? (
                    <img src={image2Original} alt="Second original" />
                  ) : (
                    <div className="placeholder">Upload Image 2</div>
                  )}
                </div>
              </div>
              <div className="image-panel compact">
                <h4>Equalized</h4>
                <div className="image-container small">
                  {image2Processed ? (
                    <img src={image2Processed} alt="Second equalized" />
                  ) : (
                    <div className="placeholder">
                      {loading.img2 ? 'Processing...' : 'Not processed'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homework3Question4;
